import type { ModalLocation } from '@ecomm/cdp-tracking'
import { Modal } from '@ecomm/shared-components/lib/Modal'
import { InformationIcon } from '@ecomm/shared-icons'
import { useTrackHomepageComparisonChartModal } from '@ecomm/tracking/useTrackHomepage'
import type { AnchorHTMLAttributes } from 'react'
import { useState } from 'react'
import { Trans } from 'react-i18next'

type InfoButtonModalProps = {
  readonly title: string
  readonly description: string | readonly string[]
  readonly modalLocation: ModalLocation
}

function CustomLink({
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a {...props}>{children}</a>
}

function InfoButtonIcon({
  title,
  description,
  modalLocation
}: InfoButtonModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const trackInfoModalOpen = useTrackHomepageComparisonChartModal(modalLocation)

  const handleInfoOnClick = () => {
    setIsOpen(true)
    trackInfoModalOpen()
  }

  return (
    <>
      <button
        aria-label={`Open description for ${title}`}
        className="cursor-pointer border-none bg-transparent p-0 align-middle lg:px-1"
        onClick={handleInfoOnClick}
      >
        <InformationIcon className="text-complementary-blue-100 h-5 w-5 md:h-6 md:w-6" />
      </button>
      <Modal
        isOpen={isOpen}
        onRequestClose={() => {
          setIsOpen(false)
        }}
      >
        <div className="prose flex max-w-sm flex-col p-8 lg:max-w-xl">
          <h3
            className="mb-4 font-bold"
            data-component="content_headline"
            data-testid="content_headline"
          >
            {title}
          </h3>
          {typeof description === 'string' ? (
            <p
              className="text-base lg:text-lg"
              data-component="content_description"
            >
              <Trans
                defaults={description}
                components={{ a: <CustomLink /> }}
              />
            </p>
          ) : (
            description.map((desc, index) => (
              <p
                className="text-base lg:text-lg"
                data-component="content_description"
                key={index}
              >
                <Trans defaults={desc} components={{ a: <CustomLink /> }} />
              </p>
            ))
          )}
          <button
            className="rounded-base bg-btn-primary text-body-size hover:bg-btn-dark cursor-pointer border-none px-4 py-3 text-white"
            onClick={() => {
              setIsOpen(false)
            }}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  )
}

export default InfoButtonIcon
